import * as T from 'three';

type GroundProbe = (position:T.Vector3, point:T.Vector3, normal:T.Vector3)=>boolean;

/** The dance has continuous foot support. Preserve its authored pelvis journey,
 * then remove the small sole-height error introduced by retargeting/body shape.
 * No leg reposing: the knee dips and weight shifts remain the animation's own. */
export class DanceContact {
  private meshes:readonly T.SkinnedMesh[]=[];
  private samples:{mesh:T.SkinnedMesh;index:number;side:number}[]=[];
  private readonly soles=[new T.Vector3(),new T.Vector3()];
  private readonly point=new T.Vector3();
  private readonly floor=new T.Vector3();
  private readonly normal=new T.Vector3();
  private readonly up=new T.Vector3();
  private readonly rotation=new T.Quaternion();

  apply(group:T.Object3D,body:T.Object3D,skeleton:T.Skeleton,meshes:readonly T.SkinnedMesh[],probe:GroundProbe,weight:number):void {
    if(meshes.length!==this.meshes.length||meshes.some((mesh,i)=>mesh!==this.meshes[i])){
      this.meshes=[...meshes];this.samples=[];
      const feet=new Map(skeleton.bones.flatMap((bone,i)=>/^(foot|ball)_[lr]$/.test(bone.name)?[[i,bone.name.endsWith('_l')?0:1] as const]:[]));
      for(const mesh of meshes){
        const indices=mesh.geometry.attributes.skinIndex,weights=mesh.geometry.attributes.skinWeight;
        if(!indices||!weights)continue;
        for(let i=0;i<indices.count;i++){
          let w=0,side=0;for(let k=0;k<4;k++){const s=feet.get(indices.getComponent(i,k));if(s!==undefined){w+=weights.getComponent(i,k);side=s;}}
          if(w>.5)this.samples.push({mesh,index:i,side});
        }
      }
    }
    group.updateMatrixWorld(true);skeleton.update();
    group.getWorldQuaternion(this.rotation);this.up.set(0,1,0).applyQuaternion(this.rotation);
    const low=[Infinity,Infinity];
    for(const {mesh,index,side}of this.samples){
      if(!mesh.visible)continue;
      mesh.getVertexPosition(index,this.point).applyMatrix4(mesh.matrixWorld);
      const h=this.point.dot(this.up);if(h<low[side]){low[side]=h;this.soles[side].copy(this.point);}
    }
    let gap=Infinity;
    for(let i=0;i<2;i++)if(Number.isFinite(low[i])&&probe(this.soles[i],this.floor,this.normal))
      gap=Math.min(gap,this.point.copy(this.soles[i]).sub(this.floor).dot(this.up));
    // Limit this to retargeting clearance; never drag an airborne pose to a floor.
    if(Number.isFinite(gap))body.position.y-=T.MathUtils.clamp(gap,-.08,.08)*T.MathUtils.clamp(weight,0,1);
  }
}
